import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { convert as htmlToText } from 'html-to-text';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Mail, ExternalLink, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SendEmailDialog = ({ open, onClose, leadId, leadEmail, leadName, leadCompany, leadPhone }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isHtml, setIsHtml] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTemplates();
      // Reset form when dialog opens
      setSelectedTemplate('');
      setSubject('');
      setBody('');
      setIsHtml(false);
    }
  }, [open]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email-templates`, {
        withCredentials: true
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load email templates');
    }
  };

  const applyMergeFields = (text) => {
    if (!text) return '';
    
    return text
      .replace(/{{name}}/gi, leadName || '')
      .replace(/{{email}}/gi, leadEmail || '')
      .replace(/{{company}}/gi, leadCompany || '')
      .replace(/{{phone}}/gi, leadPhone || '');
  };

  const stripHtml = (html) => {
    if (!html) return '';
    
    // Convert HTML to well-formatted plain text
    // Preserves line breaks, spacing, lists, and visual structure
    return htmlToText(html, {
      wordwrap: false,
      preserveNewlines: true,
      selectors: [
        { selector: 'p', options: { trailingLineBreaks: 2 } },
        { selector: 'br', format: 'lineBreak' },
        { selector: 'h1', options: { uppercase: false, trailingLineBreaks: 2 } },
        { selector: 'h2', options: { uppercase: false, trailingLineBreaks: 2 } },
        { selector: 'ul', options: { itemPrefix: '• ' } },
        { selector: 'ol', options: { itemPrefix: ' ' } },
        { selector: 'li', options: { trailingLineBreaks: 1 } },
        { selector: 'a', options: { ignoreHref: false } },
        { selector: 'strong', format: 'inline' },
        { selector: 'b', format: 'inline' },
        { selector: 'em', format: 'inline' },
        { selector: 'i', format: 'inline' }
      ]
    });
  };

  const handleTemplateChange = async (templateId) => {
    setSelectedTemplate(templateId);
    
    if (!templateId || templateId === 'none') {
      setSubject('');
      setBody('');
      setIsHtml(false);
      return;
    }

    const template = templates.find(t => t.id === templateId);
    if (template) {
      // Apply merge fields
      const processedSubject = applyMergeFields(template.subject);
      const processedBody = applyMergeFields(template.body);
      
      setSubject(processedSubject);
      setBody(processedBody);
      setIsHtml(template.html || false);
    }
  };

  const handleOpenInEmailClient = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Please enter subject and body');
      return;
    }

    if (!leadEmail) {
      toast.error('Lead email is missing');
      return;
    }

    try {
      // Log the email action in backend
      await axios.post(
        `${API}/leads/${leadId}/log-email`,
        {
          to_email: leadEmail,
          subject: subject,
          body: isHtml ? stripHtml(body) : body,
          template_id: selectedTemplate || null
        },
        { withCredentials: true }
      );

      // Create mailto link with encoded parameters
      const mailtoBody = isHtml ? stripHtml(body) : body;
      const mailtoLink = `mailto:${encodeURIComponent(leadEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(mailtoBody)}`;
      
      // Open in user's email client
      window.location.href = mailtoLink;
      
      toast.success('Opening in your email client...');
      
      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
      }, 500);

    } catch (error) {
      console.error('Failed to log email action:', error);
      toast.error(error.response?.data?.detail || 'Failed to record email action');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Compose Email to {leadName || leadEmail}
          </DialogTitle>
        </DialogHeader>
        
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Opens in your personal email client</p>
            <p className="text-xs text-blue-600 mt-1">
              This will open your default email application (Gmail, Outlook, etc.) with the email pre-filled. 
              The action will be recorded in the lead's activity log.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label>To</Label>
            <Input value={leadEmail || 'No email available'} disabled />
          </div>

          <div>
            <Label>Use Template (Optional)</Label>
            <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a template or write custom" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Template (Custom Email)</SelectItem>
                {templates.map(template => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                    {template.html && <Badge className="ml-2 text-xs" variant="secondary">HTML</Badge>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Subject *</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
            />
          </div>

          <div>
            <Label>Message *</Label>
            <Textarea
              value={isHtml ? stripHtml(body) : body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Email body (merge fields will be replaced)"
              rows={12}
              className="font-mono text-sm"
            />
            {isHtml && (
              <p className="text-xs text-amber-600 mt-1">
                Note: HTML formatting will be converted to plain text for mailto: link
              </p>
            )}
          </div>

          {/* Available Merge Fields */}
          <div className="text-sm text-slate-600">
            <p className="font-medium mb-1">Available merge fields:</p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">{'{{name}}'}</Badge>
              <Badge variant="outline">{'{{email}}'}</Badge>
              <Badge variant="outline">{'{{company}}'}</Badge>
              <Badge variant="outline">{'{{phone}}'}</Badge>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleOpenInEmailClient}>
            <ExternalLink className="w-4 h-4 mr-2" />
            Open in Email Client
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendEmailDialog;
